import React, { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { getDataRequest } from '../../api/serviceMethods';
import {
  NEWS_GET_URL,
  PRODUCTS_KNOWLEDGE_URL,
  TECHNICALS_URL,
  PRODUCT_URL,
  TEACHER_URL,
  COURSE_URL,
  FASHION_URL,
  MAGAZINES_URL
} from '../../api/URLs';

export const MANAGEMENT_TYPES = [
  "Magazine",           // /magazine
  "News Article",       // /news/NEWS0042
  "Product Details",    // /product/PRD1785749315945314
  "Product Knowledge",  // /product-knowledge/PK16800006
  "Technique",          // /technical/TC16800021
  "Fashion",            // /fashion/detail
  "Speaker's Profile",  // /online-class/68edd77422c8f0c6b1a31885 //detail teacher
  "Course"              // /online-class-member/0568247327 //detail course
];

export const LINK_TARGET_TYPES = {
  MAGAZINE: 'Magazine',
  NEWS: 'News Article',
  PRODUCT_DETAILS: 'Product Details',
  PRODUCT_MANAGEMENT: 'Product Details',
  PRODUCT_KNOWLEDGE: 'Product Knowledge',
  TECHNIQUE: 'Technique',
  TECHNOLOGY: 'Technique',
  FASHION: 'Fashion',
  SPEAKER: "Speaker's Profile",
  TEACHER: "Speaker's Profile",
  COURSE: 'Course',
  PRODUCT: 'Product Details'
};

// Helper to normalize any incoming type string to the standard enum value
export const normalizeType = (type) => {
  if (!type || typeof type !== 'string') return null;
  const t = type.trim();
  if (t === 'Magazine' || t === 'magazines' || t === 'Magazine Management' || t.toLowerCase() === 'magazine' || t.toLowerCase() === 'magazines') return LINK_TARGET_TYPES.MAGAZINE;
  if (t === 'News Article' || t === 'news_posts' || t === 'New Management' || t.toLowerCase() === 'news' || t.toLowerCase() === 'news article' || t.toLowerCase() === 'news_posts' || t.toLowerCase() === 'news-posts') return LINK_TARGET_TYPES.NEWS;
  if (t === 'Product Details' || t === 'products' || t === 'Product Management' || t.toLowerCase() === 'product' || t.toLowerCase() === 'products' || t.toLowerCase() === 'product details') return LINK_TARGET_TYPES.PRODUCT_DETAILS;
  if (t === 'Product Knowledge' || t === 'productKnowledges' || t.toLowerCase() === 'productknowledge' || t.toLowerCase() === 'product-knowledge' || t.toLowerCase() === 'productknowledges') return LINK_TARGET_TYPES.PRODUCT_KNOWLEDGE;
  if (t === 'Technique' || t === 'technicals' || t === 'Technology Display' || t.toLowerCase() === 'technology' || t.toLowerCase() === 'technicals' || t.toLowerCase() === 'technical' || t.toLowerCase() === 'technique') return LINK_TARGET_TYPES.TECHNIQUE;
  if (t === 'Fashion' || t === 'fashions' || t === 'Featured Works' || t.toLowerCase() === 'fashion' || t.toLowerCase() === 'fashions') return LINK_TARGET_TYPES.FASHION;
  if (t === "Speaker's Profile" || t === 'teachers' || t === 'Education Management' || t.toLowerCase() === 'education' || t.toLowerCase() === 'teacher' || t.toLowerCase() === 'teachers' || t.toLowerCase() === 'speaker' || t.toLowerCase() === "speaker's profile") return LINK_TARGET_TYPES.SPEAKER;
  if (t === 'Course' || t === 'courses' || t === 'Course Management' || t.toLowerCase() === 'course' || t.toLowerCase() === 'courses') return LINK_TARGET_TYPES.COURSE;
  return t;
};

// Normalize teacher ID if teacherCode is given
export const normalizeTeacherId = (id) => {
  if (!id) return '';
  const str = String(id).trim();
  if (str === '0250212874') return '68edd77422c8f0c6b1a31885';
  return str;
};

// Helper to determine target type and code from a linkUrl
export const parseInternalLink = (url) => {
  if (!url || typeof url !== 'string') {
    return { type: null, code: '' };
  }
  let trimmed = url.trim();
  if (!trimmed) {
    return { type: null, code: '' };
  }

  // If full absolute URL, parse and extract pathname if it matches internal routes
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const parsed = new URL(trimmed);
      trimmed = parsed.pathname || '';
    } catch (e) {
      return { type: null, code: '' };
    }
  }

  if (trimmed.startsWith('/news/')) {
    return { type: LINK_TARGET_TYPES.NEWS, code: trimmed.replace('/news/', '') };
  }
  if (trimmed.startsWith('/product-knowledge/')) {
    return { type: LINK_TARGET_TYPES.PRODUCT_KNOWLEDGE, code: trimmed.replace('/product-knowledge/', '') };
  }
  if (trimmed.startsWith('/technical/')) {
    return { type: LINK_TARGET_TYPES.TECHNIQUE, code: trimmed.replace('/technical/', '') };
  }
  if (trimmed.startsWith('/product/')) {
    return { type: LINK_TARGET_TYPES.PRODUCT_DETAILS, code: trimmed.replace('/product/', '') };
  }
  if (trimmed.startsWith('/online-class-member/')) {
    return { type: LINK_TARGET_TYPES.COURSE, code: trimmed.replace('/online-class-member/', '') };
  }
  if (trimmed.startsWith('/online-class/')) {
    return { type: LINK_TARGET_TYPES.SPEAKER, code: normalizeTeacherId(trimmed.replace('/online-class/', '')) };
  }
  if (trimmed.startsWith('/fashion/')) {
    return { type: LINK_TARGET_TYPES.FASHION, code: trimmed.replace('/fashion/', '') };
  }
  if (trimmed.startsWith('/magazine/')) {
    return { type: LINK_TARGET_TYPES.MAGAZINE, code: trimmed.replace('/magazine/', '') };
  }
  if (trimmed === '/magazine') {
    return { type: LINK_TARGET_TYPES.MAGAZINE, code: '' };
  }
  // Fallback for bare product ids (e.g. PRD...)
  if (trimmed.startsWith('PRD') || trimmed.startsWith('prd')) {
    return { type: LINK_TARGET_TYPES.PRODUCT_DETAILS, code: trimmed };
  }
  return { type: null, code: '' };
};

// Filter helper to exclude expired news items (expiryAt < today)
const isNewsNotExpired = (item) => {
  if (!item?.expiryAt) return true;
  const expiry = new Date(item.expiryAt);
  if (isNaN(expiry.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDay = new Date(expiry);
  expiryDay.setHours(23, 59, 59, 999);
  return expiryDay >= today;
};

// Helper to safely extract list from API response
const extractList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.list)) return res.data.list;
  if (Array.isArray(res?.list)) return res.list;
  return [];
};

export default function AdsSettingInternalLinkSelector({
  value, // can be object { type, targetType, targetId, linkUrl, linkId } or linkUrl string
  onChange,
  className = '',
  disabled = false
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'cn';

  // Helper to extract localized text
  const getLocalizedText = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (item.name) return item.name;
    if (item.title) return item.title;
    if (item.magazineName) return item.magazineName;
    if (item.localize) {
      if (item.localize[currentLang]?.title) return item.localize[currentLang].title;
      if (item.localize[currentLang]?.name) return item.localize[currentLang].name;
      const firstLang = Object.keys(item.localize)[0];
      if (firstLang && item.localize[firstLang]?.title) return item.localize[firstLang].title;
      if (firstLang && item.localize[firstLang]?.name) return item.localize[firstLang].name;
    }
    return '';
  };

  // Determine initial selected type and code
  const initialLinkUrl = typeof value === 'string' ? value : value?.linkUrl || '';
  const parsed = parseInternalLink(initialLinkUrl);
  const rawIncomingType = typeof value === 'object' ? (value?.type || value?.targetType) : null;
  const initialType = normalizeType(rawIncomingType) || parsed.type || null;
  const isTeacherTarget = (t) => t === LINK_TARGET_TYPES.SPEAKER || t === LINK_TARGET_TYPES.TEACHER;

  const getCleanCode = (typeVal, valObj, parsedVal) => {
    const isT = isTeacherTarget(typeVal);
    let c = '';
    if (typeof valObj === 'object' && valObj) {
      c = valObj?.linkId || valObj?.targetId || parsedVal || '';
    } else {
      c = parsedVal || valObj || '';
    }
    return isT ? normalizeTeacherId(c) : c;
  };

  const initialCode = getCleanCode(initialType, value, parsed.code);

  const [selectedType, setSelectedType] = useState(initialType || null);
  const [selectedCode, setSelectedCode] = useState(initialCode || '');
  const [loading, setLoading] = useState(false);
  const [optionsMap, setOptionsMap] = useState({});

  // Sync external changes
  useEffect(() => {
    const rawUrl = typeof value === 'string' ? value : value?.linkUrl || '';
    const rawIncomingType = typeof value === 'object' ? (value?.type || value?.targetType) : null;
    const normalized = normalizeType(rawIncomingType);
    if (rawUrl || normalized) {
      const p = parseInternalLink(rawUrl);
      const incomingType = normalized || p.type || null;
      const incomingCode = getCleanCode(incomingType, value, p.code);
      if (incomingType !== selectedType) {
        setSelectedType(incomingType || null);
      }
      setSelectedCode(incomingCode || '');
    } else {
      setSelectedType(null);
      setSelectedCode('');
    }
  }, [value]);

  // Fetch items for the active type
  useEffect(() => {
    let isMounted = true;
    const fetchTypeItems = async () => {
      if (!selectedType) return;

      setLoading(true);
      try {
        let items = [];
        if (selectedType === LINK_TARGET_TYPES.NEWS) {
          const res = await getDataRequest(NEWS_GET_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list
            .filter(isNewsNotExpired)
            .map(item => {
              const code = item.newsId || item._id;
              const resolvedId = String(item._id || item.id || code);
              const title = getLocalizedText(item) || code;
              return {
                label: title,
                value: resolvedId,
                linkUrl: `/news/${code}`,
                linkId: resolvedId,
                targetId: code,
                code: code,
                raw: item
              };
            });
        } else if (selectedType === LINK_TARGET_TYPES.PRODUCT_KNOWLEDGE) {
          const res = await getDataRequest(PRODUCTS_KNOWLEDGE_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.productKnowledgeId || item.id || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = getLocalizedText(item) || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/product-knowledge/${code}`,
              linkId: resolvedId,
              targetId: code,
              code: code,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.TECHNIQUE || selectedType === LINK_TARGET_TYPES.TECHNOLOGY) {
          const res = await getDataRequest(TECHNICALS_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.technicalsId || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = getLocalizedText(item) || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/technical/${code}`,
              linkId: resolvedId,
              targetId: code,
              code: code,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.PRODUCT_DETAILS || selectedType === LINK_TARGET_TYPES.PRODUCT_MANAGEMENT || selectedType === LINK_TARGET_TYPES.PRODUCT) {
          const res = await getDataRequest(PRODUCT_URL, { status: true, pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.productId || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = item.name || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/product/${code}`,
              linkId: resolvedId,
              productId: item.productId || item._id,
              targetId: code,
              code: code,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.SPEAKER || selectedType === LINK_TARGET_TYPES.TEACHER) {
          const res = await getDataRequest(TEACHER_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const resolvedId = normalizeTeacherId(String(item._id || item.linkId || item.id || ''));
            const title = item.name || item.title || getLocalizedText(item) || resolvedId;
            const teacherCode = String(item.teacherId || item.teacherNo || item.code || '');
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/online-class/${resolvedId}`,
              linkId: resolvedId,
              targetId: resolvedId,
              code: resolvedId,
              teacherCode: teacherCode,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.COURSE) {
          const res = await getDataRequest(COURSE_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.courseId || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = item.title || item.name || getLocalizedText(item) || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/online-class-member/${code}`,
              linkId: resolvedId,
              targetId: code,
              code: code,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.FASHION) {
          const res = await getDataRequest(FASHION_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.fashionId || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = item.title || item.name || getLocalizedText(item) || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/fashion/${code}`,
              linkId: resolvedId,
              targetId: code,
              code: code,
              raw: item
            };
          });
        } else if (selectedType === LINK_TARGET_TYPES.MAGAZINE) {
          const res = await getDataRequest(MAGAZINES_URL, { pageNo: 1, pageSize: 150 });
          const list = extractList(res);
          items = list.map(item => {
            const code = item.magazineId || item._id;
            const resolvedId = String(item._id || item.id || code);
            const title = item.title || item.name || item.magazineName || getLocalizedText(item) || code;
            return {
              label: title,
              value: resolvedId,
              linkUrl: `/magazine/${code}`,
              linkId: resolvedId,
              targetId: code,
              code: code,
              raw: item
            };
          });
        }

        if (isMounted) {
          setOptionsMap(prev => ({ ...prev, [selectedType]: items }));
        }
      } catch (err) {
        console.error('AdsSettingInternalLinkSelector fetch error:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTypeItems();
    return () => { isMounted = false; };
  }, [selectedType, currentLang]);

  const currentOptions = (selectedType && optionsMap[selectedType]) ? optionsMap[selectedType] : [];

  const typeOptions = MANAGEMENT_TYPES.map(type => ({
    label: t(type) || type,
    value: type
  }));

  // Helper to find option matching either value, linkId, targetId, code, teacherCode, or raw properties
  const findMatchingOption = (val) => {
    if (!val || !currentOptions || currentOptions.length === 0) return null;
    const str = String(val).trim();
    if (!str) return null;
    const isT = isTeacherTarget(selectedType);
    const norm = isT ? normalizeTeacherId(str) : str;
    return currentOptions.find(opt =>
      String(opt.value) === norm ||
      String(opt.linkId) === norm ||
      String(opt.targetId) === norm ||
      String(opt.code) === norm ||
      String(opt.value) === str ||
      String(opt.linkId) === str ||
      String(opt.targetId) === str ||
      String(opt.code) === str ||
      (opt.linkUrl && opt.linkUrl === str) ||
      (opt.teacherCode && String(opt.teacherCode) === str) ||
      (opt.raw?._id && String(opt.raw._id) === str) ||
      (opt.raw?.id && String(opt.raw.id) === str) ||
      (opt.raw?.productKnowledgeId && String(opt.raw.productKnowledgeId) === str) ||
      (opt.raw?.technicalsId && String(opt.raw.technicalsId) === str) ||
      (opt.raw?.productId && String(opt.raw.productId) === str) ||
      (opt.raw?.newsId && String(opt.raw.newsId) === str) ||
      (opt.raw?.magazineId && String(opt.raw.magazineId) === str) ||
      (opt.raw?.courseId && String(opt.raw.courseId) === str)
    );
  };

  const findMatchingOptionForState = () => {
    if (!currentOptions || currentOptions.length === 0) return null;
    let matched = findMatchingOption(selectedCode);
    if (matched) return matched;
    if (typeof value === 'object' && value) {
      if (value.linkId) matched = findMatchingOption(value.linkId);
      if (!matched && value.targetId) matched = findMatchingOption(value.targetId);
      if (!matched && value.linkUrl) {
        matched = findMatchingOption(value.linkUrl);
        if (!matched) {
          const p = parseInternalLink(value.linkUrl);
          if (p.code) matched = findMatchingOption(p.code);
        }
      }
    } else if (typeof value === 'string' && value) {
      matched = findMatchingOption(value);
      if (!matched) {
        const p = parseInternalLink(value);
        if (p.code) matched = findMatchingOption(p.code);
      }
    }
    return matched;
  };

  // Auto-align selectedCode if options have linkId matching selectedCode
  useEffect(() => {
    if (!selectedCode && !value) return;
    if (currentOptions.length > 0) {
      const found = findMatchingOptionForState();
      if (found) {
        const resolvedLinkId = String(found?.raw?._id || found?.linkId || found?.value || '');
        if (selectedCode !== resolvedLinkId) {
          setSelectedCode(resolvedLinkId);
          if (onChange) {
            const isTeacher = selectedType === LINK_TARGET_TYPES.SPEAKER || selectedType === LINK_TARGET_TYPES.TEACHER;
            const resolvedTargetId = isTeacher
              ? normalizeTeacherId(resolvedLinkId)
              : String(found?.targetId || found?.code || resolvedLinkId);
            onChange({
              type: selectedType,
              targetType: selectedType,
              targetId: resolvedTargetId,
              linkUrl: found?.linkUrl || (isTeacher ? `/online-class/${resolvedTargetId}` : ''),
              linkId: resolvedLinkId, // Strictly _id for all types!
              productId: found?.productId || found?.raw?.productId || null,
              linkType: 'internal',
              rawItem: found?.raw || null
            });
          }
        }
      }
    }
  }, [currentOptions, selectedType, selectedCode]);

  const handleTypeChange = (newType) => {
    setSelectedType(newType || null);
    setSelectedCode('');
    if (onChange) {
      onChange({
        type: newType || null,
        targetType: newType || null,
        targetId: '',
        linkUrl: '',
        linkId: null,
        linkType: 'internal',
        rawItem: null
      });
    }
  };

  const handleItemSelect = (selectedVal, option) => {
    const isTeacher = selectedType === LINK_TARGET_TYPES.SPEAKER || selectedType === LINK_TARGET_TYPES.TEACHER;
    const found = option || findMatchingOption(selectedVal);
    const resolvedLinkId = String(found?.raw?._id || found?.linkId || found?.value || selectedVal || '');
    const resolvedTargetId = isTeacher
      ? normalizeTeacherId(resolvedLinkId)
      : String(found?.targetId || found?.code || resolvedLinkId);
    const resolvedLinkUrl = found?.linkUrl || (isTeacher ? `/online-class/${resolvedTargetId}` : '');

    setSelectedCode(resolvedLinkId);

    if (onChange) {
      onChange({
        type: selectedType,
        targetType: selectedType,
        targetId: resolvedTargetId,
        linkUrl: resolvedLinkUrl,
        linkId: resolvedLinkId, // Strictly _id for all types!
        productId: found?.productId || found?.raw?.productId || null,
        linkType: 'internal',
        rawItem: found?.raw || null
      });
    }
  };

  return (
    <div className={`internal-link-selector ${className}`} style={{ width: '100%' }}>
      {/* Content Type Selector */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#4B5563', marginBottom: '4px' }}>
          {t('Select Content Type') || 'Select Content Type'}:
        </div>
        <Select
          allowClear
          style={{ width: '100%' }}
          disabled={disabled}
          value={selectedType || undefined}
          onChange={handleTypeChange}
          options={typeOptions}
          placeholder={t('Select Content Type') || 'Select Content Type'}
        />
      </div>

      {/* Target Item Searchable Dropdown */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#4B5563', marginBottom: '4px' }}>
          {t('Select Item to Link') || 'Select Item to Link'}:
        </div>
        <Select
          showSearch
          allowClear
          disabled={disabled || !selectedType}
          loading={loading}
          style={{ width: '100%' }}
          value={(() => {
            const matched = findMatchingOptionForState();
            if (matched) return matched.value;
            if (loading) return undefined;
            return selectedCode || undefined;
          })()}
          onChange={handleItemSelect}
          placeholder={selectedType ? (t('Please select') || 'Please select') : (t('Please select content type first') || 'Please select content type first')}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
            (option?.value ?? '').toLowerCase().includes(input.toLowerCase()) ||
            (option?.code ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={currentOptions}
          notFoundContent={loading ? <Spin size="small" /> : null}
        />
      </div>
    </div>
  );
}
